/**
 * CBERD Material and Construction Database
 * Sources: CBERD Wall, Roof, Floor IDF files + Glass material.txt
 *
 * Key exports:
 *  - MATERIAL_DEFS / AIRGAP_DEFS  — raw EnergyPlus Material objects for IDF output
 *  - WALL_CONSTRUCTIONS / ROOF_CONSTRUCTIONS / FLOOR_CONSTRUCTIONS — with U-values + colours
 *  - ALL_GLAZING_TYPES — simple types (for RETV/EPF) + selected CBERD products
 *  - calcOverhangPF / calcFinPF — physical dimension → projection factor
 *  - getConstructionColor / getConstructionU / getGlazingById — lookup helpers
 */

// ── Frame material U-values ───────────────────────────────────────────────────
// Source: ISO 10077-1 / ASHRAE Fundamentals / ECBC 2024 Appendix
// Uf = frame U-value (W/m²K) — used for effective window U computation
export const FRAME_MATERIALS = [
  { id: 'wood',       label: 'Wood / Timber',                   uf: 2.0 },
  { id: 'steel-notb', label: 'Steel (no thermal break)',         uf: 5.9 },
  { id: 'steel-tb',   label: 'Steel (with thermal break)',       uf: 3.0 },
  { id: 'alum-notb',  label: 'Aluminium (no thermal break)',     uf: 5.9 },
  { id: 'alum-tb',    label: 'Aluminium (with thermal break)',   uf: 2.8 },
  { id: 'alum-itb',   label: 'Aluminium (improved TB)',          uf: 2.4 },
  { id: 'pvc',        label: 'PVC (3-chamber)',                  uf: 2.2 },
  { id: 'upvc',       label: 'uPVC (5-chamber)',                 uf: 1.8 },
  { id: 'composite',  label: 'Composite (wood-aluminium)',       uf: 2.2 },
];

export function getFrameUValue(frameId) {
  return FRAME_MATERIALS.find((f) => f.id === frameId)?.uf ?? 2.8;
}

/**
 * Effective window U-value considering glazing fraction and frame U.
 * U_eff = glazingFrac × U_glass + (1 - glazingFrac) × U_frame
 */
export function calcEffectiveWindowU(glazingU, frameU, glazingAreaPct = 80) {
  const gf = Math.min(1, Math.max(0, glazingAreaPct / 100));
  return gf * glazingU + (1 - gf) * frameU;
}

/**
 * Effective SHGC — frame contribution is negligible (≈0 for opaque frames).
 * SHGC_eff = glazingFrac × SHGC_glass
 */
export function calcEffectiveSHGC(glazingSHGC, glazingAreaPct = 80) {
  return (Math.min(1, Math.max(0, glazingAreaPct / 100))) * glazingSHGC;
}

// ── Shading Projection Factor utilities ──────────────────────────────────────
// ENS 2024 / ECBC 2024 definition:
//   Overhang: PF = depth / (gap + windowHeight)
//     depth = horizontal projection of overhang (m)
//     gap   = vertical distance from underside of overhang to top of window (m)
//   Fin:     PF = depth / (depth + gap)
//     depth = horizontal fin projection (m)
//     gap   = lateral distance from fin base to near edge of window (m)

export function calcOverhangPF(depth = 0, gap = 0, windowHeight = 1.5) {
  if (depth <= 0) return 0;
  const H = (gap || 0) + Math.max(0.1, windowHeight || 1.5);
  return Math.min(1.5, depth / H);
}

export function calcFinPF(depth = 0, gap = 0) {
  if (depth <= 0) return 0;
  const W = (depth || 0) + Math.max(0, gap || 0);
  return W > 0 ? Math.min(1.5, depth / W) : 0;
}

const ZERO_SHADE = { depth: 0, gap: 0 };

/**
 * Resolve a window's EFFECTIVE shading from its `shading_type` selector.
 *
 * `shading_type` is the single source of truth for WHICH shading elements are
 * active; the overhang/fin_left/fin_right depth+gap values are retained in the
 * data even when an element is deselected (so toggling back restores them). This
 * helper masks out the inactive elements so every consumer (3D render, RETV, EPF,
 * IDF export) agrees on exactly what shading exists — fixing the bug where
 * switching e.g. "overhang + both fins" → "left fin only" left stale overhang/
 * right-fin depths that all the depth-gated consumers kept drawing/counting.
 *
 * shading_type values: 'none' | 'overhang' | 'fin_left' | 'fin_right'
 *   | 'overhang_fin_left' | 'overhang_fin_right' | 'overhang_fins'
 *
 * @param {Object} w  window entry with { shading_type, overhang, fin_left, fin_right }
 * @returns {{ overhang, fin_left, fin_right }} with inactive elements zeroed
 */
export function resolveActiveShading(w) {
  const st = w?.shading_type || 'none';
  const hasOverhang = st.includes('overhang');
  const hasFinLeft  = st.includes('fin_left')  || st === 'overhang_fins';
  const hasFinRight = st.includes('fin_right') || st === 'overhang_fins';
  return {
    overhang:  hasOverhang ? (w.overhang  || ZERO_SHADE) : ZERO_SHADE,
    fin_left:  hasFinLeft  ? (w.fin_left  || ZERO_SHADE) : ZERO_SHADE,
    fin_right: hasFinRight ? (w.fin_right || ZERO_SHADE) : ZERO_SHADE,
  };
}

// ── Raw material data (EnergyPlus Material objects) ──────────────────────────
// Names match CBERD IDF files exactly — do not change without updating construction layers.
export const MATERIAL_DEFS = {
  // ── Plasters ──────────────────────────────────────────────────────────────
  'Cement plaster - 4mm':   { roughness:'MediumRough', thickness:0.004, conductivity:1.208, density:1880, specificHeat:516.97 },
  'Cement plaster - 12mm':  { roughness:'MediumRough', thickness:0.012, conductivity:1.208, density:1880, specificHeat:516.97 },
  'Cement plaster - 12 mm': { roughness:'MediumRough', thickness:0.012, conductivity:1.208, density:1880, specificHeat:517.0  },
  'Cement plaster - 18mm':  { roughness:'MediumRough', thickness:0.018, conductivity:1.208, density:1880, specificHeat:516.97 },
  'Cement screed - 50mm':   { roughness:'Rough',       thickness:0.050, conductivity:1.208, density:1880, specificHeat:517.0  },
  'Cement screed - 25mm':   { roughness:'Rough',       thickness:0.025, conductivity:1.208, density:1880, specificHeat:517.0  },
  // ── Masonry ───────────────────────────────────────────────────────────────
  'Fired Red Brick - 230mm':  { roughness:'Rough', thickness:0.230, conductivity:0.589, density:1660, specificHeat:163.25 },
  'Fired Red Brick - 110mm':  { roughness:'Rough', thickness:0.110, conductivity:0.589, density:1660, specificHeat:163.25 },
  'Fired Red Brick - 75mm':   { roughness:'Rough', thickness:0.075, conductivity:0.589, density:1660, specificHeat:163.0  },
  'Flyash Brick - 230mm':     { roughness:'Rough', thickness:0.230, conductivity:0.639, density:1240, specificHeat:659.03 },
  'Flyash Brick - 110mm':     { roughness:'Rough', thickness:0.110, conductivity:0.639, density:1240, specificHeat:659.03 },
  'Autodaved aerated concrete block(AAC) - 200mm': { roughness:'MediumRough', thickness:0.200, conductivity:0.184, density:642, specificHeat:236.76 },
  'Autodaved aerated concrete block(AAC) - 100mm': { roughness:'MediumRough', thickness:0.100, conductivity:0.184, density:642, specificHeat:236.76 },
  'Compressed mud blocks(CEB) - 230mm':    { roughness:'Rough', thickness:0.230, conductivity:1.212, density:1840, specificHeat:322.72 },
  'Compressed Mud Blocks(CEB) - 110mm':   { roughness:'Rough', thickness:0.110, conductivity:1.212, density:1840, specificHeat:322.72 },
  'Concrete blocks - 200mm':  { roughness:'Rough', thickness:0.200, conductivity:1.396, density:2427, specificHeat:195.76 },
  'Concrete blocks - 100mm':  { roughness:'Rough', thickness:0.100, conductivity:1.396, density:2427, specificHeat:195.76 },
  'Concrete block with EPS balls - 150mm': { roughness:'Rough', thickness:0.150, conductivity:0.394, density:1160, specificHeat:939.66 },
  'Sandstone - 300mm':        { roughness:'MediumSmooth', thickness:0.300, conductivity:3.010, density:2530, specificHeat:630.71 },
  'RCC - 230mm':              { roughness:'Rough', thickness:0.230, conductivity:1.411, density:2349, specificHeat:299.0 },
  'RCC - 110mm':              { roughness:'Rough', thickness:0.110, conductivity:1.411, density:2349, specificHeat:299.0 },
  'RCC slab - 100mm':         { roughness:'Rough', thickness:0.100, conductivity:1.411, density:2349, specificHeat:299.0 },
  // ── Cladding ──────────────────────────────────────────────────────────────
  'Sandstone cladding - 15mm':    { roughness:'MediumSmooth', thickness:0.015, conductivity:3.010, density:2530, specificHeat:631.0 },
  'Clay tile cladding - 15mm':    { roughness:'Smooth',       thickness:0.015, conductivity:0.632, density:2531, specificHeat:563.0 },
  'Jaisalmer yellow stone - 15mm':{ roughness:'Smooth',       thickness:0.015, conductivity:2.745, density:3006, specificHeat:697.0 },
  'Engineered wood - 15mm':       { roughness:'Smooth',       thickness:0.015, conductivity:0.253, density:571,  specificHeat:2492.0 },
  'AC sheet shera - 15mm':        { roughness:'Smooth',       thickness:0.015, conductivity:0.513, density:1377, specificHeat:875.0 },
  'Aluminum - 0.5mm':  { roughness:'VerySmooth', thickness:0.001, conductivity:212.2, density:2700, specificHeat:907.0 },
  'Aluminum - 1mm':    { roughness:'VerySmooth', thickness:0.001, conductivity:212.2, density:2700, specificHeat:907.0 },
  'Rubber - 3mm':      { roughness:'MediumRough', thickness:0.003, conductivity:0.056, density:89, specificHeat:1670.0 },
  // ── Insulation ────────────────────────────────────────────────────────────
  'Expanded Polystrene (EPS) - 25mm':  { roughness:'MediumSmooth', thickness:0.025, conductivity:0.033, density:30.94, specificHeat:1745.0 },
  'Expanded Polystrene (EPS) - 50mm':  { roughness:'MediumSmooth', thickness:0.050, conductivity:0.033, density:30.94, specificHeat:1745.0 },
  'Extruded Polystrene (XPS) - 30mm':  { roughness:'MediumSmooth', thickness:0.030, conductivity:0.032, density:30, specificHeat:1247.0 },
  'Extruded Polystrene (XPS) - 50mm':  { roughness:'MediumSmooth', thickness:0.050, conductivity:0.032, density:30, specificHeat:1247.0 },
  'Extruded Polystyrene (XPS)  - 30mm':{ roughness:'MediumSmooth', thickness:0.030, conductivity:0.032, density:30, specificHeat:1247.0 },
  'Glasswool - 25mm':   { roughness:'Rough', thickness:0.025, conductivity:0.035, density:49, specificHeat:692.0 },
  'Glasswool - 50mm':   { roughness:'Rough', thickness:0.050, conductivity:0.035, density:49, specificHeat:692.0 },
  'Rockwool - 25mm':    { roughness:'Rough', thickness:0.025, conductivity:0.045, density:96, specificHeat:1134.0 },
  'Rockwool - 50mm':    { roughness:'Rough', thickness:0.050, conductivity:0.045, density:96, specificHeat:1134.0 },
  'Polyurathene foam(PUF) - 25mm':   { roughness:'MediumSmooth', thickness:0.025, conductivity:0.037, density:40, specificHeat:1760.0 },
  'Polyurathene foam(PUF) - 50 mm':  { roughness:'MediumSmooth', thickness:0.050, conductivity:0.037, density:40, specificHeat:1760.0 },
  'Polyurathene foam - 50mm':        { roughness:'MediumSmooth', thickness:0.050, conductivity:0.037, density:40, specificHeat:1760.0 },
  'Gypsum board - 12mm': { roughness:'Smooth', thickness:0.012, conductivity:0.253, density:623, specificHeat:968.0 },
  // ── Roof-specific ─────────────────────────────────────────────────────────
  'Mild Steel(MS) sheet - 0.5mm':              { roughness:'Smooth', thickness:0.0005, conductivity:44.117, density:7823, specificHeat:536.0 },
  'Mild Steel(MS) sheet - 4mm':                { roughness:'Smooth', thickness:0.004,  conductivity:44.117, density:7823, specificHeat:535.5 },
  'Galvanised Iron sheet - 0.5mm':             { roughness:'Smooth', thickness:0.0005, conductivity:64.689, density:7850, specificHeat:333.0 },
  'Fibre Reinforced Plastic(FRP) Sheet - 1mm': { roughness:'Smooth', thickness:0.001,  conductivity:0.225,  density:1183, specificHeat:1431.0 },
  'Acrylic sheet - 4mm': { roughness:'VerySmooth', thickness:0.004, conductivity:0.217, density:1145, specificHeat:1383.0 },
  'Mangalore tile - 15mm':  { roughness:'MediumRough', thickness:0.015, conductivity:0.605, density:2531, specificHeat:506.0 },
  'Clay tile - 15mm':        { roughness:'MediumSmooth', thickness:0.015, conductivity:0.632, density:2531, specificHeat:563.0 },
  'PVC membrane - 1mm':      { roughness:'Smooth', thickness:0.001, conductivity:0.503, density:1743, specificHeat:973.0 },
  'Soil - 200mm':            { roughness:'Rough', thickness:0.200, conductivity:0.278, density:1410, specificHeat:823.0 },
  'Ceramic Tile - 6mm':      { roughness:'VerySmooth', thickness:0.006, conductivity:1.372, density:2707, specificHeat:446.0 },
  'Marble - 15mm':           { roughness:'Smooth', thickness:0.015, conductivity:2.372, density:2650, specificHeat:954.0 },
  // ── Floor-specific ────────────────────────────────────────────────────────
  'Vitrified tile - 12mm':   { roughness:'VerySmooth', thickness:0.012, conductivity:1.4786, density:2719, specificHeat:663.8 },
  'Oak Laminated floor tiles - 6mm': { roughness:'MediumSmooth', thickness:0.006, conductivity:0.2652, density:949, specificHeat:1410.9 },
  'Granite - 15mm':          { roughness:'VerySmooth', thickness:0.015, conductivity:2.622, density:2690, specificHeat:711.5 },
  'Kota Stone - 15 mm':      { roughness:'Smooth', thickness:0.015, conductivity:3.0229, density:3102, specificHeat:668.3 },
};

export const AIRGAP_DEFS = {
  'F04 Wall air space resistance':     { thermalResistance: 0.15 },
  'F05 Ceiling air space resistance':  { thermalResistance: 0.18 },
};

// ── Wall Constructions ────────────────────────────────────────────────────────
// uValue in W/m²K (pre-calculated: Rsi=0.13, Rso=0.04 + layer resistances)
export const WALL_CONSTRUCTIONS = [
  // ── Fired Red Brick ───────────────────────────────────────────────────────
  { id:'frb-230', name:'Fired Red Brick Wall - 230mm', category:'Fired Red Brick',
    layers:['Cement plaster - 18mm','Fired Red Brick - 230mm','Cement plaster - 12mm'], uValue:1.71, color:'#c1440e' },
  { id:'frb-110', name:'Fired Red Brick Wall - 110mm', category:'Fired Red Brick',
    layers:['Cement plaster - 18mm','Fired Red Brick - 110mm','Cement plaster - 12mm'], uValue:2.62, color:'#c1440e' },
  { id:'frb-stone', name:'Fired Red Brick Wall with Stone Cladding', category:'Fired Red Brick',
    layers:['Sandstone cladding - 15mm','Cement plaster - 12mm','Fired Red Brick - 110mm','Cement plaster - 12mm'], uValue:2.62, color:'#9a8870' },
  { id:'frb-clay', name:'Fired Red Brick Wall with Clay tile Cladding', category:'Fired Red Brick',
    layers:['Clay tile cladding - 15mm','Cement plaster - 12mm','Fired Red Brick - 110mm','Cement plaster - 12mm'], uValue:2.55, color:'#c07050' },
  { id:'frb-cavity', name:'Fired Red Brick Wall with Cavity and Air Gap', category:'Fired Red Brick',
    layers:['Cement plaster - 18mm','Fired Red Brick - 110mm','F04 Wall air space resistance','Fired Red Brick - 110mm','Cement plaster - 12mm'], uValue:1.39, color:'#c1440e' },
  { id:'frb-xps30-out', name:'Fired Red Brick Wall with 30mm XPS Outside', category:'Fired Red Brick',
    layers:['Cement plaster - 4mm','Extruded Polystrene (XPS) - 30mm','Fired Red Brick - 110mm','Cement plaster - 12mm'], uValue:0.76, color:'#c1440e' },
  { id:'frb-xps50-out', name:'Fired Red Brick Wall with 50mm XPS Outside', category:'Fired Red Brick',
    layers:['Cement plaster - 4mm','Extruded Polystrene (XPS) - 50mm','Fired Red Brick - 110mm','Cement plaster - 12mm'], uValue:0.52, color:'#c1440e' },
  { id:'frb-xps30-in', name:'Fired Red Brick Wall with 30mm XPS Inside', category:'Fired Red Brick',
    layers:['Cement plaster - 18mm','Fired Red Brick - 110mm','Extruded Polystrene (XPS) - 30mm','Cement plaster - 4mm'], uValue:0.77, color:'#c1440e' },
  { id:'frb-xps50-in', name:'Fired Red Brick Wall with 50mm XPS Inside', category:'Fired Red Brick',
    layers:['Cement plaster - 18mm','Fired Red Brick - 110mm','Extruded Polystrene (XPS) - 50mm','Cement plaster - 4mm'], uValue:0.53, color:'#c1440e' },
  { id:'frb-acp-airgap', name:'Fired Red Brick Wall with ACP Cladding and Air Gap', category:'Fired Red Brick',
    layers:['Aluminum - 0.5mm','Rubber - 3mm','Aluminum - 0.5mm','F04 Wall air space resistance','Fired Red Brick - 110mm','Cement plaster - 12mm'], uValue:1.75, color:'#b0b8c0' },
  { id:'frb-wood', name:'Fired Red Brick Wall with Engineered Wood Cladding and Air Gap', category:'Fired Red Brick',
    layers:['Engineered wood - 15mm','F04 Wall air space resistance','Fired Red Brick - 110mm','Cement plaster - 12mm'], uValue:1.35, color:'#8b5e3c' },
  // ── Flyash Brick ──────────────────────────────────────────────────────────
  { id:'flyash-230', name:'Flyash Brick Wall - 230mm', category:'Flyash Brick',
    layers:['Cement plaster - 18mm','Flyash Brick - 230mm','Cement plaster - 12mm'], uValue:1.80, color:'#c8a048' },
  { id:'flyash-110', name:'Flyash Brick Wall - 110mm', category:'Flyash Brick',
    layers:['Cement plaster - 18mm','Flyash Brick - 110mm','Cement plaster - 12mm'], uValue:2.70, color:'#c8a048' },
  { id:'flyash-stone', name:'Flyash Brick Wall with Stone Cladding', category:'Flyash Brick',
    layers:['Sandstone cladding - 15mm','Cement plaster - 12mm','Flyash Brick - 110mm','Cement plaster - 12mm'], uValue:2.65, color:'#9a8870' },
  // ── AAC Block ─────────────────────────────────────────────────────────────
  { id:'aac-200', name:'AAC Block Wall - 200mm', category:'AAC Block',
    layers:['Cement plaster - 18mm','Autodaved aerated concrete block(AAC) - 200mm','Cement plaster - 12mm'], uValue:0.78, color:'#d0cfc8' },
  { id:'aac-100', name:'AAC Block Wall - 100mm', category:'AAC Block',
    layers:['Cement plaster - 18mm','Autodaved aerated concrete block(AAC) - 100mm','Cement plaster - 12mm'], uValue:1.36, color:'#d0cfc8' },
  { id:'aac-stone', name:'AAC Block Wall with Stone Cladding', category:'AAC Block',
    layers:['Sandstone cladding - 15mm','Cement plaster - 12mm','Autodaved aerated concrete block(AAC) - 100mm','Cement plaster - 12mm'], uValue:1.38, color:'#9a8870' },
  // ── Compressed Mud Block ──────────────────────────────────────────────────
  { id:'ceb-230', name:'Compressed Mud Block Wall - 230mm', category:'Compressed Mud Block',
    layers:['Cement plaster - 18mm','Compressed mud blocks(CEB) - 230mm','Cement plaster - 12mm'], uValue:2.60, color:'#9a7255' },
  { id:'ceb-110', name:'Compressed Mud Block Wall - 110mm', category:'Compressed Mud Block',
    layers:['Cement plaster - 18mm','Compressed Mud Blocks(CEB) - 110mm','Cement plaster - 12mm'], uValue:3.30, color:'#9a7255' },
  // ── Concrete Block ────────────────────────────────────────────────────────
  { id:'concrete-200', name:'Concrete Block Wall - 200mm', category:'Concrete Block',
    layers:['Cement plaster - 18mm','Concrete blocks - 200mm','Cement plaster - 12mm'], uValue:2.96, color:'#989890' },
  { id:'concrete-100', name:'Concrete Block Wall - 100mm', category:'Concrete Block',
    layers:['Cement plaster - 18mm','Concrete blocks - 100mm','Cement plaster - 12mm'], uValue:4.14, color:'#989890' },
  { id:'concrete-eps', name:'Concrete Wall with EPS balls', category:'Concrete Block',
    layers:['Cement plaster - 18mm','Concrete block with EPS balls - 150mm','Cement plaster - 12mm'], uValue:1.32, color:'#989890' },
  { id:'concrete-stone', name:'Concrete Block with Stone Cladding', category:'Concrete Block',
    layers:['Sandstone cladding - 15mm','Cement plaster - 12mm','Concrete blocks - 100mm','Cement plaster - 12mm'], uValue:3.60, color:'#9a8870' },
  // ── RCC ───────────────────────────────────────────────────────────────────
  { id:'rcc-230', name:'RCC Wall - 230mm', category:'RCC',
    layers:['Cement plaster - 18mm','RCC - 230mm','Cement plaster - 12mm'], uValue:2.80, color:'#909090' },
  { id:'rcc-110', name:'RCC Wall - 110mm', category:'RCC',
    layers:['Cement plaster - 18mm','RCC - 110mm','Cement plaster - 12mm'], uValue:3.67, color:'#909090' },
  // ── Sandstone ─────────────────────────────────────────────────────────────
  { id:'sandstone-300', name:'Sandstone wall - 300mm', category:'Sandstone',
    layers:['Cement plaster - 18mm','Sandstone - 300mm','Cement plaster - 12mm'], uValue:3.40, color:'#d4a840' },
];

// ── Roof Constructions ────────────────────────────────────────────────────────
export const ROOF_CONSTRUCTIONS = [
  { id:'rcc-cement', name:'RCC Slab with Cement Sceed', category:'RCC Slab',
    layers:['Cement screed - 50mm','RCC slab - 100mm','Cement plaster - 12mm'], uValue:3.42 },
  { id:'rcc-xps50-pvc', name:'RCC Slab with 50 mm XPS Insulation and PVC Membrane', category:'RCC Slab',
    layers:['PVC membrane - 1mm','Extruded Polystrene (XPS) - 50mm','RCC slab - 100mm','Cement plaster - 12mm'], uValue:0.55 },
  { id:'rcc-xps30-mosaic', name:'RCC Slab with 30 mm XPS Insulation and China Mosaic', category:'RCC Slab',
    layers:['Ceramic Tile - 6mm','Cement screed - 50mm','Extruded Polystyrene (XPS)  - 30mm','PVC membrane - 1mm','RCC slab - 100mm','Cement plaster - 12mm'], uValue:0.73 },
  { id:'rcc-marble', name:'RCC Slab with Marble Stone', category:'RCC Slab',
    layers:['Marble - 15mm','Cement screed - 50mm','PVC membrane - 1mm','RCC slab - 100mm','Cement plaster - 12mm'], uValue:2.45 },
  { id:'rcc-soil', name:'RCC Slab with Soil on Top', category:'RCC Slab',
    layers:['Soil - 200mm','PVC membrane - 1mm','RCC slab - 100mm','Cement plaster - 12mm'], uValue:0.48 },
  { id:'ms-sheet', name:'MS Sheet Sloping Roof', category:'Metal Sheet',
    layers:['Mild Steel(MS) sheet - 0.5mm'], uValue:5.88 },
  { id:'ms-glasswool', name:'MS Sheet Roof with Glasswool Insulation', category:'Metal Sheet',
    layers:['Mild Steel(MS) sheet - 0.5mm','Glasswool - 50mm','Mild Steel(MS) sheet - 0.5mm'], uValue:0.63 },
  { id:'ms-glasswool-gypsum', name:'MS Sheet Roof with Glasswool and Gypsum Board Ceiling', category:'Metal Sheet',
    layers:['Mild Steel(MS) sheet - 0.5mm','Glasswool - 50mm','Gypsum board - 12mm'], uValue:0.61 },
  { id:'ms-airgap-gypsum', name:'MS Sheet Roof with Air Gap and Gypsum Board Ceiling', category:'Metal Sheet',
    layers:['Mild Steel(MS) sheet - 0.5mm','F05 Ceiling air space resistance','Gypsum board - 12mm'], uValue:1.85 },
  { id:'gi-sheet', name:'GI Sheet Sloping Roof', category:'Metal Sheet',
    layers:['Galvanised Iron sheet - 0.5mm'], uValue:5.88 },
  { id:'gi-glasswool-gypsum', name:'GI Sheet with Glasswool and Gypsum Board Ceiling', category:'Metal Sheet',
    layers:['Galvanised Iron sheet - 0.5mm','Glasswool - 50mm','Gypsum board - 12mm'], uValue:0.61 },
  { id:'frp-sheet', name:'FRP Sheet Sloping Roof', category:'FRP Sheet',
    layers:['Fibre Reinforced Plastic(FRP) Sheet - 1mm'], uValue:4.80 },
  { id:'frp-glasswool-gypsum', name:'FRP Sheet with Glasswool and Gypsum Board Ceiling', category:'FRP Sheet',
    layers:['Fibre Reinforced Plastic(FRP) Sheet - 1mm','Glasswool - 50mm','Gypsum board - 12mm'], uValue:0.61 },
  { id:'mangalore-tile', name:'Mangalore Tile Sloping Roof', category:'Tile',
    layers:['Mangalore tile - 15mm'], uValue:2.00 },
  { id:'frb-vault-230', name:'Fired Red Brick Vault - 230mm - with China Mosaic', category:'Brick Vault',
    layers:['Ceramic Tile - 6mm','Cement screed - 25mm','Fired Red Brick - 230mm','Cement plaster - 12mm'], uValue:1.50 },
];

// ── Floor Constructions ───────────────────────────────────────────────────────
export const FLOOR_CONSTRUCTIONS = [
  { id:'floor-ips', name:'RCC Floor with IPS Finish', category:'RCC Floor',
    layers:['Cement screed - 50mm','Mild Steel(MS) sheet - 4mm'], uValue:3.00 },
  { id:'floor-vitrified', name:'RCC Floor with Vitrified Tile', category:'RCC Floor',
    layers:['Vitrified tile - 12mm','Cement screed - 50mm','RCC slab - 100mm','Cement plaster - 12 mm'], uValue:2.13 },
  { id:'floor-marble', name:'RCC Floor with Marble Stone Finish', category:'RCC Floor',
    layers:['Marble - 15mm','Cement screed - 50mm','RCC slab - 100mm','Cement plaster - 12 mm'], uValue:2.10 },
  { id:'floor-granite', name:'RCC Floor with Granite Stone Finish', category:'RCC Floor',
    layers:['Granite - 15mm','Cement screed - 50mm','RCC slab - 100mm','Cement plaster - 12 mm'], uValue:2.10 },
  { id:'floor-kota', name:'RCC Floor with Kota Stone Finish', category:'RCC Floor',
    layers:['Kota Stone - 15 mm','Cement screed - 50mm','RCC slab - 100mm','Cement plaster - 12 mm'], uValue:2.10 },
  { id:'floor-wooden', name:'RCC Floor with Wooden Flooring', category:'RCC Floor',
    layers:['Oak Laminated floor tiles - 6mm','Vitrified tile - 12mm','Cement screed - 50mm','RCC slab - 100mm','Cement plaster - 12 mm'], uValue:2.05 },
  { id:'floor-pvc', name:'RCC Floor with Vitrified Tile and PVC Membrane Finish', category:'RCC Floor',
    layers:['PVC membrane - 1mm','Vitrified tile - 12mm','Cement screed - 50mm','RCC slab - 100mm','Cement plaster - 12 mm'], uValue:2.10 },
];

// ── Glazing Types (Simple + CBERD) ────────────────────────────────────────────
// Simple types are for RETV/EPF use with WindowMaterial:SimpleGlazingSystem in IDF.
// CBERD types use full WindowMaterial:Glazing spectral data.
export const ALL_GLAZING_TYPES = [
  // ── Simple types (backward compat) ────────────────────────────────────────
  { id:'single-clear',      name:'Single Clear (6mm)',     uValue:5.80, shgc:0.82, category:'Single Pane',
    idfType:'simple', vlt:0.88 },
  { id:'single-tinted',     name:'Single Tinted (6mm)',    uValue:5.70, shgc:0.60, category:'Single Pane',
    idfType:'simple', vlt:0.65 },
  { id:'single-reflective', name:'Single Reflective (6mm)',uValue:5.50, shgc:0.35, category:'Single Pane',
    idfType:'simple', vlt:0.22 },
  { id:'double-clear',      name:'DGU Clear',              uValue:2.80, shgc:0.70, category:'Double Pane',
    idfType:'simple', vlt:0.78 },
  { id:'double-lowe',       name:'DGU Low-E',              uValue:1.80, shgc:0.42, category:'Double Pane',
    idfType:'simple', vlt:0.55 },
  { id:'double-lowe-argon', name:'DGU Low-E + Argon',      uValue:1.40, shgc:0.38, category:'Double Pane',
    idfType:'simple', vlt:0.52 },
  { id:'triple-lowe',       name:'Triple Low-E',           uValue:0.90, shgc:0.30, category:'Triple Pane',
    idfType:'simple', vlt:0.48 },
  // ── CBERD glazing products ────────────────────────────────────────────────
  // uValue ≈ 5.7 W/m²K for 6mm single pane (Rsi=0.13, glass, Rso=0.04)
  // SHGC ≈ SolarTransmittance × 1.02 (approximate)
  { id:'cberd-planilux-6', name:'SG-Planilux_6 (Clear Float 6mm)', uValue:5.68, shgc:0.82, category:'CBERD Clear',
    idfType:'spectral', idfName:'SG-Planilux_6',
    thickness:0.00595, solarT:0.806, frontSolarR:0.072, backSolarR:0.071,
    visT:0.887, frontVisR:0.080, backVisR:0.080, infraredT:0, frontEmiss:0.84, backEmiss:0.84, conductivity:1.0 },
  { id:'cberd-planilux-8', name:'SG-Planilux_8 (Clear Float 8mm)', uValue:5.69, shgc:0.79, category:'CBERD Clear',
    idfType:'spectral', idfName:'SG-Planilux_8',
    thickness:0.00791, solarT:0.772, frontSolarR:0.069, backSolarR:0.068,
    visT:0.878, frontVisR:0.078, backVisR:0.078, infraredT:0, frontEmiss:0.84, backEmiss:0.84, conductivity:1.0 },
  { id:'cberd-ais-clear-6', name:'AIS-Clear Taloja_6 (Clear 6mm)', uValue:5.68, shgc:0.74, category:'CBERD Clear',
    idfType:'spectral', idfName:'AIS-Clear Taloja_6',
    thickness:0.00581, solarT:0.728, frontSolarR:0.066, backSolarR:0.067,
    visT:0.869, frontVisR:0.079, backVisR:0.079, infraredT:0, frontEmiss:0.84, backEmiss:0.84, conductivity:1.0 },
  { id:'cberd-evolite-clear-6', name:'SG-Evolite Clear_6 (Low-Iron 6mm)', uValue:5.60, shgc:0.38, category:'CBERD Low-Iron',
    idfType:'spectral', idfName:'SG-Evolite Clear_6',
    thickness:0.00596, solarT:0.374, frontSolarR:0.183, backSolarR:0.118,
    visT:0.495, frontVisR:0.092, backVisR:0.135, infraredT:0, frontEmiss:0.497, backEmiss:0.84, conductivity:1.0 },
  { id:'cberd-evolite-blue-6', name:'SG-Evolite Blue_6 (Blue Tinted 6mm)', uValue:5.68, shgc:0.21, category:'CBERD Tinted',
    idfType:'spectral', idfName:'SG-Evolite Blue_6',
    thickness:0.00591, solarT:0.205, frontSolarR:0.179, backSolarR:0.068,
    visT:0.318, frontVisR:0.085, backVisR:0.083, infraredT:0, frontEmiss:0.51, backEmiss:0.84, conductivity:1.0 },
  { id:'cberd-parsol-dark-grey-5', name:'SG-Parsol Dark Grey_5 (Dark Grey 5mm)', uValue:5.68, shgc:0.34, category:'CBERD Tinted',
    idfType:'spectral', idfName:'SG-Parsol Dark Grey_5',
    thickness:0.00493, solarT:0.338, frontSolarR:0.045, backSolarR:0.045,
    visT:0.195, frontVisR:0.040, backVisR:0.041, infraredT:0, frontEmiss:0.84, backEmiss:0.84, conductivity:1.0 },
  { id:'cberd-reflectasol-db-4', name:'SG-Reflectasol Dark Blue_4 (Reflective 4mm)', uValue:5.68, shgc:0.26, category:'CBERD Reflective',
    idfType:'spectral', idfName:'SG-Reflectasol Dark Blue_4',
    thickness:0.00389, solarT:0.256, frontSolarR:0.373, backSolarR:0.134,
    visT:0.143, frontVisR:0.536, backVisR:0.147, infraredT:0, frontEmiss:0.852, backEmiss:0.84, conductivity:1.0 },
  { id:'cberd-titanium-blue-6', name:'SG-Titanium Blue_6 (Metallic Blue 6mm)', uValue:5.68, shgc:0.18, category:'CBERD Reflective',
    idfType:'spectral', idfName:'SG-Titanium Blue_6',
    thickness:0.0059, solarT:0.180, frontSolarR:0.386, backSolarR:0.179,
    visT:0.216, frontVisR:0.281, backVisR:0.208, infraredT:0, frontEmiss:0.655, backEmiss:0.84, conductivity:1.0 },
  { id:'cberd-dark-grey-6', name:'AIS-Dark Grey Tinted_6 (Dark Grey 6mm)', uValue:5.68, shgc:0.32, category:'CBERD Tinted',
    idfType:'spectral', idfName:'AIS-Dark Grey Tinted_6',
    thickness:0.00584, solarT:0.315, frontSolarR:0.044, backSolarR:0.046,
    visT:0.157, frontVisR:0.040, backVisR:0.042, infraredT:0, frontEmiss:0.84, backEmiss:0.84, conductivity:1.0 },
  { id:'cberd-ecosense-jade-6', name:'AIS-Ecosense Enhance Jade_6 (Green 6mm)', uValue:5.68, shgc:0.16, category:'CBERD Solar Control',
    idfType:'spectral', idfName:'AIS-Ecosense Enhance Jade_6',
    thickness:0.00579, solarT:0.157, frontSolarR:0.325, backSolarR:0.107,
    visT:0.207, frontVisR:0.342, backVisR:0.192, infraredT:0, frontEmiss:0.841, backEmiss:0.84, conductivity:1.0 },
];

// ── Lookup helpers ────────────────────────────────────────────────────────────

export function getWallConstructionById(id) {
  return WALL_CONSTRUCTIONS.find((c) => c.id === id) ?? null;
}

export function getRoofConstructionById(id) {
  return ROOF_CONSTRUCTIONS.find((c) => c.id === id) ?? null;
}

export function getFloorConstructionById(id) {
  return FLOOR_CONSTRUCTIONS.find((c) => c.id === id) ?? null;
}

export function getGlazingById(id) {
  return ALL_GLAZING_TYPES.find((g) => g.id === id) ?? null;
}

export function getConstructionColor(constructionId) {
  return WALL_CONSTRUCTIONS.find((c) => c.id === constructionId)?.color ?? '#c8c4bc';
}

export function getConstructionUValue(constructionId) {
  return WALL_CONSTRUCTIONS.find((c) => c.id === constructionId)?.uValue ?? 2.21;
}

// Returns U-value for any glazing type (simple or CBERD)
export function getGlazingUValue(glazingId) {
  return ALL_GLAZING_TYPES.find((g) => g.id === glazingId)?.uValue ?? 5.80;
}

export function getGlazingSHGC(glazingId) {
  return ALL_GLAZING_TYPES.find((g) => g.id === glazingId)?.shgc ?? 0.82;
}

export function getGlazingVLT(glazingId) {
  const vlt = ALL_GLAZING_TYPES.find((g) => g.id === glazingId)?.vlt;
  return vlt != null ? Math.round(vlt * 100) : null; // returns 0-100 integer or null
}

export function calcCustomConstructionU(constr) {
  if (!constr) return null;
  if (constr.source === 'layers' && constr.layers?.length) {
    const R = 0.17 + constr.layers.reduce(
      (s, l) => s + ((l.thickness || 0.001) / (l.conductivity || 0.1)), 0
    );
    return R > 0 ? 1 / R : null;
  }
  return constr.u_value ?? constr.computedU ?? null;
}

// Collect all unique Material layer names referenced by a list of construction IDs
export function collectUsedMaterials(wallConstrIds = [], roofConstrIds = [], floorConstrIds = []) {
  const names = new Set();
  const airgapNames = new Set();
  const allConstrs = [
    ...wallConstrIds.map((id) => WALL_CONSTRUCTIONS.find((c) => c.id === id)),
    ...roofConstrIds.map((id) => ROOF_CONSTRUCTIONS.find((c) => c.id === id)),
    ...floorConstrIds.map((id) => FLOOR_CONSTRUCTIONS.find((c) => c.id === id)),
  ].filter(Boolean);

  for (const constr of allConstrs) {
    for (const layer of constr.layers) {
      if (AIRGAP_DEFS[layer]) airgapNames.add(layer);
      else if (MATERIAL_DEFS[layer]) names.add(layer);
    }
  }
  return { materialNames: [...names], airgapNames: [...airgapNames] };
}
